/**
 * Settings → Security (docs/plans/ADMIN_SETTINGS_SCOPE_2026_09_29.md, WS-D).
 *
 * Session length for everyone, plus where 2-step verification and session
 * management actually live. Sign-in rules (Google, pre-created accounts) are
 * owned by the Google sign-in work, PR #4227.
 *
 * Endpoints: GET /api/admin/settings?category=security, PUT/DELETE
 * /api/admin/settings/:key (via SettingField).
 */
import React from 'react';
import { Link } from 'react-router-dom';
import { Clock, KeyRound, Shield } from 'lucide-react';
import { QueryClient, QueryClientProvider, useQuery } from '@tanstack/react-query';
import { adminFetch } from '../../../utils/adminApi';
import { SettingField } from './SettingField';
import { AdminMe, SettingsResponse, adminCan, errorMessage } from './settingsLogic';

const queryClient = new QueryClient({
  defaultOptions: { queries: { staleTime: 20_000, refetchOnWindowFocus: false } },
});

const SecurityInner: React.FC = () => {
  const me = useQuery({
    queryKey: ['admin', 'me'],
    queryFn: async () => (await adminFetch<{ admin: AdminMe }>('/api/admin/auth/me')).admin,
  });
  const settings = useQuery({
    queryKey: ['admin', 'settings', 'security'],
    queryFn: () => adminFetch<SettingsResponse>('/api/admin/settings?category=security'),
  });
  const canEdit = adminCan(me.data, 'settings.write');

  return (
    <div className="mx-auto max-w-4xl p-4 sm:p-6">
      <h1 className="flex items-center gap-2 text-xl font-bold text-gray-900">
        <Shield className="h-5 w-5" style={{ color: '#1F6C4F' }} /> Security
      </h1>
      <p className="mt-1 text-sm text-gray-500">
        How long portal sessions last, and where sign-in security is managed. Every change is recorded in the audit log.
      </p>

      {settings.isLoading && <div className="mt-6 text-sm text-gray-500">Loading…</div>}
      {settings.error && (
        <div className="mt-6 rounded-md bg-red-50 p-3 text-sm text-red-700">{errorMessage(settings.error)}</div>
      )}

      {settings.data && (
        <section className="mt-6 rounded-lg border border-gray-200 bg-white p-4">
          <h2 className="flex items-center gap-2 text-sm font-semibold uppercase tracking-wide text-gray-500">
            <Clock className="h-4 w-4" /> Sessions
          </h2>
          {settings.data.settings.map(s => (
            <SettingField key={s.key} setting={s} canEdit={canEdit} onChanged={() => settings.refetch()} />
          ))}
          <p className="pt-3 text-xs text-gray-500">
            See and end your own sessions in <Link to="/admin/me#sessions" className="text-[#1F6C4F] hover:underline">My preferences</Link>.
            To sign a teammate out everywhere, open them in <Link to="/admin/team" className="text-[#1F6C4F] hover:underline">Team</Link> → Sign-in.
          </p>
        </section>
      )}

      <section className="mt-6 rounded-lg border border-gray-200 bg-white p-4">
        <h2 className="flex items-center gap-2 text-sm font-semibold uppercase tracking-wide text-gray-500">
          <KeyRound className="h-4 w-4" /> 2-step verification
        </h2>
        <p className="mt-3 text-sm text-gray-700">
          The portal has no 2-step code of its own. When you sign in with Google, your Google Workspace account's
          2-step verification applies, so require it for everyone in the Google Workspace admin console.
        </p>
      </section>
    </div>
  );
};

const SettingsSecurity: React.FC = () => (
  <QueryClientProvider client={queryClient}>
    <SecurityInner />
  </QueryClientProvider>
);

export default SettingsSecurity;
